import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query';
import type { SavedAudience } from '../lib/audience-definition';
import { mergeSavedIntoPicker, type PickerList } from '../lib/audience-picker';
import {
  archiveSavedAudience,
  createSavedAudience,
  fetchSavedAudiences,
  updateSavedAudience,
  type CreateSavedAudienceArgs,
  type SavedAudienceListResponse,
  type UpdateSavedAudienceArgs,
} from '../services/saved-audiences';

/**
 * The switcher's data (NPJB-711 / 722 / 723).
 *
 * **A read failure is not an error state.** NPJB-711 AC11 says the rest of Intelligence
 * must render when the saved audiences cannot be read: the control falls back to the
 * TAM and the list is empty. So `useSavedAudiencesQuery` never throws to a boundary —
 * callers read `audiences`, which is `[]` on failure, and the page carries on.
 */

const listQuery = {
  queryKey: queryKeys.savedAudiences.list(),
  queryFn: fetchSavedAudiences,
};

export const useSavedAudiencesQuery = () => {
  const query = useQuery({
    ...listQuery,
    // One retry, not three. The switcher is chrome — a seller who opened it wants an
    // answer now, and a slow triple-retry looks like a hang rather than a degraded list.
    retry: 1,
  });
  return {
    /** Every audience, active first then archived. `[]` while loading or on failure. */
    audiences: query.data?.items ?? [],
    isLoading: query.isLoading,
    /** True when the list could not be read — AC11's degraded state, not a crash. */
    failed: query.isError,
  };
};

/** Refetch the list after any write. One helper so no mutation forgets. */
const useInvalidate = () => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: queryKeys.savedAudiences.all });
};

/** The list with `saved` in it: replaced where it was, else first. */
export const upsertSavedAudience = (
  items: readonly SavedAudience[],
  saved: SavedAudience,
): SavedAudience[] =>
  items.some((a) => a.id === saved.id)
    ? items.map((a) => (a.id === saved.id ? saved : a))
    : [saved, ...items];

/**
 * After a create or an update: write the row the server returned into the cached list, THEN
 * refetch. The list is slow to come back (every archived row travels with it), and a refetch
 * alone left a window where reopening the drawer could not find the audience just saved and
 * opened as "new" (filters-drawer AC8).
 *
 * The audience page's picker lists take the row too (NPJB-800 AC3): its chips read the open
 * audience from there, so a sources Apply shows the new label at once, not after the refetch.
 */
const useWriteThrough = () => {
  const queryClient = useQueryClient();
  return (saved: SavedAudience) => {
    queryClient.setQueryData<SavedAudienceListResponse>(listQuery.queryKey, (old) =>
      old === undefined ? old : { ...old, items: upsertSavedAudience(old.items, saved) },
    );
    queryClient.setQueriesData<PickerList>(
      { queryKey: queryKeys.savedAudiences.pickers() },
      (old) => (old === undefined ? old : mergeSavedIntoPicker(old, saved)),
    );
    return queryClient.invalidateQueries({ queryKey: queryKeys.savedAudiences.all });
  };
};

/**
 * The user's own saves of an audience's definition — the drawer's Save and the sources
 * Apply. The audience page shows "Updating results…" while one is pending for the open
 * audience. The background size write uses its own key, so it never shows that line.
 */
export const SAVE_AUDIENCE_MUTATION_KEY = [...queryKeys.savedAudiences.all, 'save'] as const;
const SIZE_MUTATION_KEY = [...queryKeys.savedAudiences.all, 'size'] as const;

/**
 * The list once it has loaded — for a decision that must not run on an empty list: the name
 * check and "which audience is open". A save pressed while the list is still loading would
 * otherwise see no audiences, pass the name check, and get the server's 409 instead of the
 * refusal (filters-drawer AC9). A load already in flight is joined, not repeated.
 */
export const useLoadedSavedAudiences = () => {
  const queryClient = useQueryClient();
  return useCallback(
    async (): Promise<SavedAudience[]> => (await queryClient.ensureQueryData(listQuery)).items,
    [queryClient],
  );
};

export const useCreateSavedAudienceMutation = () => {
  const writeThrough = useWriteThrough();
  // A tenant travels only when the caller names one (the audience page's client picker);
  // otherwise the backend files the row under the caller's own ICP.
  return useMutation({
    mutationFn: (args: CreateSavedAudienceArgs) => createSavedAudience(args),
    onSuccess: writeThrough,
  });
};

export const useUpdateSavedAudienceMutation = ({ background = false } = {}) => {
  const writeThrough = useWriteThrough();
  return useMutation({
    mutationKey: background ? SIZE_MUTATION_KEY : SAVE_AUDIENCE_MUTATION_KEY,
    mutationFn: (args: UpdateSavedAudienceArgs) => updateSavedAudience(args),
    // Not awaited: the row is already in every cached list, and the slow list refetch would
    // hold the caller's re-read of the page back by its whole length (NPJB-800). A create
    // still waits — the new row is not in the picker until that refetch brings its ICP.
    onSuccess: (saved: SavedAudience) => {
      void writeThrough(saved);
    },
  });
};

export const useArchiveSavedAudienceMutation = () => {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: string) => archiveSavedAudience(id),
    onSuccess: invalidate,
  });
};

/** The open audience, or `undefined` when the TAM is showing. */
export const findOpenAudience = (
  audiences: SavedAudience[],
  openAudienceId: string | null,
): SavedAudience | undefined => {
  if (openAudienceId === null) return undefined;
  const match = audiences.find((a) => a.id === openAudienceId);
  // An archived audience is not offered as something to open, and if it WAS the open
  // one the control falls back to the TAM (NPJB-723 AC14). Treating it as closed here
  // is what makes that fallback automatic rather than something every caller repeats.
  return match?.isArchived === true ? undefined : match;
};

/**
 * Is `name` already held by another ACTIVE audience (NPJB-711 AC9, NPJB-723 AC9)?
 *
 * Case and the spaces around a name do not make it different. Archived rows do not hold
 * their name, which is why unarchive can be refused (NPJB-723 AC13). The backend makes
 * the same check; this copy answers before the round trip.
 */
export const isNameTaken = (audiences: SavedAudience[], name: string, skipId?: string): boolean => {
  const wanted = name.trim().toLowerCase();
  return audiences.some(
    (a) => !a.isArchived && a.id !== skipId && a.title.trim().toLowerCase() === wanted,
  );
};
